import { ArrowRight, Building2, KeyRound, MessageCircleMore } from "lucide-react";
import { Link } from "react-router-dom";
import { useSiteBranding } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";

export const LeaseOutPage = () => {
  const { buildBrandedPath, getWhatsAppHref, leadFormPath } = useSiteBranding();

  useSeo({
    title: "Lease Out | SG Prop Portal",
    description: "Plan rental pricing, tenant-fit, and leasing support before marketing your property.",
  });

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#fbf6e7_0%,#f7efdf_46%,#e4ebf2_100%)] text-[#001935]" data-testid="lease-out-page">
      <section className="section-shell pt-10">
        <div className="rounded-[36px] border border-[#001935]/10 bg-white/92 p-8 shadow-[0_22px_60px_rgba(0,25,53,0.08)] lg:p-10">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]" data-testid="lease-out-page-kicker">Lease Out</p>
          <div className="mt-3 grid gap-8 lg:grid-cols-[1fr_0.9fr] lg:items-start">
            <div>
              <h1 className="text-4xl font-semibold leading-tight text-[#001935] md:text-5xl" data-testid="lease-out-page-heading">
                Plan your rental strategy before you list.
              </h1>
              <p className="mt-4 text-sm leading-8 text-[#42566f] md:text-base" data-testid="lease-out-page-copy">
                Landlords can use SG Prop Portal as the clean handoff point for rental pricing, tenant-fit, listing timing, and direct leasing support.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a className="gold-button" data-testid="lease-out-page-whatsapp-button" href={getWhatsAppHref("Hello SG Prop Portal, I want help leasing out my property.")} rel="noreferrer" target="_blank">
                  WhatsApp Leasing Desk <ArrowRight className="ml-2 h-4 w-4" />
                </a>
                <Link className="outline-button" data-testid="lease-out-page-contact-button" to={leadFormPath}>
                  Request Leasing Support
                </Link>
              </div>
            </div>

            <div className="grid gap-4" data-testid="lease-out-page-card-grid">
              {[
                { key: "pricing", icon: Building2, title: "Rental pricing guidance" },
                { key: "tenant", icon: KeyRound, title: "Tenant-fit and positioning" },
                { key: "response", icon: MessageCircleMore, title: "Direct response path to the team" },
              ].map((card) => {
                const Icon = card.icon;
                return (
                  <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] px-5 py-5" data-testid={`lease-out-page-card-${card.key}`} key={card.key}>
                    <div className="flex items-center gap-3">
                      <div className="rounded-full border border-[#001935]/10 bg-white p-3 text-[#8b6d16]">
                        <Icon className="h-5 w-5" />
                      </div>
                      <p className="text-sm font-semibold text-[#001935]">{card.title}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell pb-14 pt-8">
        <div className="rounded-[32px] border border-[#001935]/10 bg-white/88 p-8 shadow-[0_18px_50px_rgba(0,25,53,0.06)]" data-testid="lease-out-page-next-steps-section">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Next Steps</p>
          <h2 className="mt-3 text-3xl font-semibold leading-tight text-[#001935]" data-testid="lease-out-page-next-steps-heading">
            Move from landlord intent to a live leasing plan.
          </h2>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link className="gold-button" data-testid="lease-out-page-browse-rentals-button" to={buildBrandedPath("/rent")}>
              See Rental Market <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <Link className="outline-button" data-testid="lease-out-page-pricing-button" to={buildBrandedPath("/pricing")}>
              Review Platform Value
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};